// src/features/changelog/utils/changelog.js
// Pure helpers for the schema 2 changelog served from /changelog.json

export const CHANGE_TYPES = ['feature', 'fix', 'improvement', 'note'];

// Order the groups are shown in on a release. Notes go first: they describe
// behaviour changes users need to know about before anything else.
export const GROUP_ORDER = ['note', 'feature', 'fix', 'improvement'];

const DEFAULT_TYPE = 'improvement';

/**
 * Latest full version string ("0.39.4") from a changelog document, or null.
 * Prefers the stored `latest` field, falls back to the newest change.
 */
export function getLatestVersion(data) {
  if (!data) return null;
  return data.latest ?? data.releases?.[0]?.changes?.[0]?.version ?? null;
}

function normalizeType(type) {
  return CHANGE_TYPES.includes(type) ? type : DEFAULT_TYPE;
}

function emptyCounts() {
  return Object.fromEntries(CHANGE_TYPES.map((type) => [type, 0]));
}

/**
 * Normalize a changelog document into what the page renders.
 * Throws if the document is not schema 2.
 */
export function normalizeChangelog(data) {
  if (!data || data.schema !== 2) {
    throw new Error(`Unsupported changelog schema: ${data?.schema}`);
  }

  const releases = (data.releases ?? []).map((release) => {
    const counts = emptyCounts();
    const changes = (release.changes ?? []).map((change) => {
      const type = normalizeType(change.type);
      counts[type] += 1;
      return { ...change, type };
    });

    return {
      version: release.version,
      title: release.title ?? '',
      summary: release.summary ?? '',
      changes,
      groups: GROUP_ORDER
        .map((type) => ({ type, changes: changes.filter((change) => change.type === type) }))
        .filter((group) => group.changes.length > 0),
      counts,
    };
  });

  return { latest: getLatestVersion(data), releases };
}
